/* eslint react/no-array-index-key: 0 */
import { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import S from './VehicleInfoStyles';
import useGetDriverById from '../../../../API/hooks/drivers/useGetDriverById';
import useGetVehicleById from '../../../../API/hooks/vehicles/useGetVehicleById';
import useGetDeviceByDeviceId from '../../../../API/hooks/devices/useGetDeviceByDeviceId';
import { convertMsToKm } from '../../../../utils/unitsUtils';

const generateLink = (type: string, id: string): string => {
  if (!id) {
    return '';
  }
  switch (type) {
    case 'device':
      return `/devices/${id}`;
    case 'driver':
      return `/drivers/${id}`;
    case 'trip':
    case 'video':
    case 'vehicle':
      return `/vehicles/${id}`;
    default:
      return '';
  }
};

const formatSpeedLabel = (value: number | undefined | null, entityType: string): string => {
  if (['trip', 'vehicle', 'device'].includes(entityType) && (value === null || value === undefined)) {
    return 'n/a';
  }
  return value !== null && value !== undefined ? `${convertMsToKm(value)} km/h` : '0 km/h';
};

const VehicleInfo = forwardRef(({
  fleetId, entityId, entityType, speedMs, showSpeedInVehicleInfo = true, showDetailsButton = true,
}: any, ref: any): JSX.Element | null => {
  const navigate = useNavigate();
  const [markerData, setVehicleData] = useState({
    licensePlate: '',
    device: '',
    driver: '',
  });

  const [driverId, setDriverId] = useState<string | undefined>(undefined);

  const checkVehicleData = ['vehicle', 'trip', 'video'].includes(entityType);
  const checkDeviceData = entityType === 'device';
  const checkDriverData = (entityType === 'driver') || !!driverId;

  const { data: vehicleData, isLoading: vehicleIsLoading } = useGetVehicleById(entityId, fleetId, checkVehicleData);
  const { data: deviceData, isLoading: deviceIsLoading } = useGetDeviceByDeviceId(entityId, fleetId, checkDeviceData);
  const { data: driverData, isLoading: driverIsLoading } = useGetDriverById(driverId || entityId, fleetId, checkDriverData);

  useEffect(() => {
    switch (entityType) {
      case 'device':
        setVehicleData({
          licensePlate: '',
          device: deviceData?.deviceId || '',
          driver: `${driverData?.firstName} ${driverData?.lastName}`,
        });
        setDriverId(deviceData?.vehicle?.driverId);
        break;
      case 'driver':
        setVehicleData({
          licensePlate: '',
          device: driverData?.device?.deviceId || '',
          driver: `${driverData?.firstName} ${driverData?.lastName}`,
        });
        break;
      case 'trip':
      case 'video':
      case 'vehicle':
        setVehicleData({
          licensePlate: vehicleData?.licensePlateNumber || '',
          device: vehicleData?.device?.deviceId || '',
          driver: `${vehicleData?.driver?.firstName} ${vehicleData?.driver?.lastName}`,
        });
        break;
      default:
        setVehicleData({
          licensePlate: '',
          device: '',
          driver: '',
        });
    }
  }, [driverData, vehicleData, deviceData, driverId, entityType]);

  const idReady = !!entityId;
  const deviceLineLoading = entityType === 'device' && idReady && deviceIsLoading;
  const plateLineLoading = ['vehicle', 'trip', 'video'].includes(entityType) && idReady && vehicleIsLoading;
  const driverLineLoading = entityType === 'driver' && idReady && driverIsLoading;

  const goToDetails = (): void => {
    if (entityType === 'device') {
      if (deviceData?._id) {
        navigate(`/devices/${deviceData._id}`);
        return;
      }
      if (entityId && /^[a-f\d]{24}$/i.test(entityId)) {
        navigate(generateLink(entityType, entityId));
      }
      return;
    }
    navigate(generateLink(entityType, entityId));
  };

  return (
    <S.InfoBox>
      <div ref={ref}>
        {(entityType === 'vehicle' || entityType === 'trip' || entityType === 'video') && (
          <p>
            <b>License plate:</b>
            {plateLineLoading ? 'data is loading...' : markerData.licensePlate}
            {showSpeedInVehicleInfo && (
              <>
                {', '}
                <b>Speed:</b>
                {formatSpeedLabel(speedMs, entityType)}
              </>
            )}
          </p>
        )}
        {entityType === 'device' && (
          <p>
            <b>Device:</b>
            {deviceLineLoading ? 'data is loading...' : markerData.device}
            {showSpeedInVehicleInfo && (
              <>
                {', '}
                <b>Speed:</b>
                {formatSpeedLabel(speedMs, entityType)}
              </>
            )}
          </p>
        )}
        {entityType === 'driver' && (
          <p><b>Driver:</b>{driverLineLoading ? 'data is loading...' : markerData.driver}</p>
        )}
        {showDetailsButton ? (
          <button
            type="button"
            onClick={goToDetails}
            disabled={entityType === 'device' && (!!idReady && deviceIsLoading)}
          >
            Show details
          </button>
        ) : <div style={{ marginTop: '10px' }} />}
      </div>
    </S.InfoBox>
  );
});

export default VehicleInfo;
